/* eslint-disable @typescript-eslint/no-explicit-any -- app instance is dynamically constructed; full App type is a future effort */
import { mergeDeepRight, map } from 'ramda';
import { evaluateResults } from 'zousan/evaluate';
import { series } from 'zousan/series';

import type { LayoutUiHide } from '@typings/layout.ts';
import type { ThemeDef } from '@typings/theme.ts';
import i18nInit from '@utils/i18n.ts';
import { startInitialStateListener } from '@utils/isInitialState.ts';

import { version as weVersion } from '../package.json';
import type { MonitoringConfig } from '../plugins/monitoring/src/monitoring.ts';
import type { PluginConfig as PostHogConfig } from '../plugins/posthog/config-schema.ts';
import debugTools from './debugTools.ts';
import { buildEnv } from './env.ts';
import { create as createBus } from './extModules/bustle.ts';
import { initLog } from './extModules/log.ts';
import type { MessageBus } from './types/bus.ts';

/** Base plugin configuration — all plugins share the `active` property */
export interface PluginConfig {
  active?: boolean | 'notLocalhost';
  [key: string]: unknown;
}

/** Application configuration as loaded from JSON config files */
export interface AppConfig {
  name: string;
  appName?: string;
  engineName?: string;
  renderDiv?: string;
  initialLayout?: string;
  defaultTheme?: string;
  desktopViewMinWidth?: number;
  mobileHighZoomBreakpoint?: number;
  setWindowTitle?: boolean;
  configPostProc?: string[];
  supportedLanguages?: string[];
  defaultLanguage?: string;
  extends?: string[];
  debug?: boolean | Record<string, unknown>;
  logFilter?: RegExp | string;
  theme?: ThemeDef;
  uiHide?: LayoutUiHide;
  plugins: Record<string, PluginConfig | false>;
  [key: string]: unknown;
}

/** The full app instance created by create() */
export interface AppInstance {
  bus: MessageBus;
  config: AppConfig;

  debug: Record<string, (...args: any[]) => unknown>;
  destroy: () => void;
  env: ReturnType<typeof buildEnv>;

  gt: () => any;
  i18n: () => unknown;
  info: { wePkg: { version: string } };
  log: ReturnType<ReturnType<typeof initLog>['sublog']>;
  plugins: Record<string, { init: () => void; [key: string]: unknown }>;
  themePack?: unknown;
  [key: string]: unknown;
}

declare global {
  interface Window {
    _app: AppInstance;
    R: typeof import('ramda');
  }
}

const isBrowser = typeof window !== 'undefined';
const TRACE = false;

async function setupPlugin(app: AppInstance, id: string, config: PluginConfig | false) {
  if (!config) {
    app.log.info(`Plugin ${id} explicitly disabled`);
    return null;
  }

  if (config.active !== undefined) {
    if (config.active === false || (config.active === 'notLocalhost' && app.env.isLocalhost())) {
      app.log.info(`Plugin ${id} explicitly deativated`);
      return null;
    }
  }

  // Vite's dynamic-import-vars treats each ${} as a single path segment,
  // so plugin ids with a scope (e.g. "online/bluedot") need to be split so
  // each variable represents one segment. RSpack used to accept the collapsed
  // form; this two-path variant works under both bundlers.
  const load = id.includes('/')
    ? (() => {
        const [scope, shortName] = id.split('/');
        return import(`../plugins/${scope}/${shortName}/src/index.ts`);
      })()
    : import(`../plugins/${id}/src/index.ts`);
  return load.then(pluginModule => {
    app.log.info(`Creating plugin ${id}`);
    return pluginModule.create(app, config);
  });
}

// takes the `lang` query parameter and returns the most specific supported language
// Recognizes the following optional configuration parameters:
//   supportedLanguages : array of supported language strings. i.e. [ "en", "en-US", "fr", "ja" ]
//   defaultLanguage : if all fails, use this language. default = "en"
const getLang = (config: AppConfig) => {
  const supportedLanguages = config.supportedLanguages || [
    'am',
    'ar',
    'de',
    'el-GR',
    'en',
    'es',
    'fr',
    'hi',
    'is',
    'it',
    'ja',
    'ko',
    'nl',
    'pl',
    'pt',
    'ru',
    'so',
    'th',
    'zh-Hans',
    'zh-Hant',
  ];

  if (typeof window !== 'undefined') {
    // if this is a browser...
    const queryParms = new URLSearchParams(location.search);
    // eslint-disable-next-line no-constant-condition
    let lang = queryParms.get('lang') || (typeof navigator ? navigator.language : null);
    while (lang) {
      if (lang && supportedLanguages.includes(lang)) {
        return lang;
      } else {
        lang = lang.substring(0, lang.lastIndexOf('-'));
      }
    }
  }

  return config.defaultLanguage || 'en';
};

async function loadConfig(name: string) {
  return isBrowser ? import(`./configs/${name}.json`) : import(`./configs/${name}.json.js`); // bit of a hack - but supports all versions of node. See https://gitlab.com/locuslabspublic/node-sdk/-/merge_requests/1
}

async function extendConfig(config: AppConfig, extendsConfigs: string[]) {
  let newConfig: AppConfig = {} as AppConfig;
  const extConfigFiles = await Promise.all(extendsConfigs.map(loadConfig));
  for (const extendsConfigMod of extConfigFiles) {
    let extendsConfig = extendsConfigMod.default;
    extendsConfig = extendsConfig.extends ? await extendConfig(extendsConfig, extendsConfig.extends) : extendsConfig; // enable recursive extends
    newConfig = mergeDeepRight(newConfig, extendsConfig); // default is JSON data in ES6 modules import
  }
  newConfig = mergeDeepRight(newConfig, config);
  return newConfig;
}

// const isSimpleName = name => /^[-a-zA-Z0-9]+$/.test(name) // composed of only alphanumeric and dash

const createPostProcessor = (name: string) => (config: AppConfig) =>
  import(`./configs/postproc-${name}.ts`).then(pp => pp.process(config));

const handleConfigPostProcess = async (config: AppConfig) =>
  config.configPostProc ? series(config, ...config.configPostProc.map(createPostProcessor)) : config;

async function create(rawConfig: AppConfig) {
  const appInstance = Object.create(null) as AppInstance;

  let config = rawConfig.extends ? await extendConfig(rawConfig, rawConfig.extends) : rawConfig;

  if (config.plugins?.monitoring && config.plugins.monitoring.active !== false) {
    const monConfig = config.plugins.monitoring as MonitoringConfig;
    try {
      const mon = await import('../plugins/monitoring/src/index.ts');
      await mon.activate(monConfig);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn('Monitoring plugin failed to load (continuing without it):', message);
    }
  }

  config = await handleConfigPostProcess(config);

  if (isBrowser && config.plugins?.posthog && config.plugins.posthog.active !== false) {
    try {
      const { activate } = await import('../plugins/posthog/src/posthog.ts');
      await activate(config.plugins.posthog as PostHogConfig);
    } catch (err) {
      console.warn('PostHog failed to start (continuing without it):', err);
    }
  }

  const lang = getLang(config);
  const i18n = await i18nInit(lang, { debug: config.debug });
  appInstance.i18n = () => i18n;
  appInstance.gt = () => i18n.t.bind(i18n); // get translation function - don't hold this, it is bound to current lang
  appInstance.config = config;

  appInstance.plugins = {};
  const appLog = initLog('web-engine', {
    enabled: !!config.debug,
    isBrowser,
    color: 'cyan',
    logFilter: config.logFilter,
    truncateObjects: !isBrowser,
    trace: TRACE,
  });

  appInstance.log = appLog.sublog(config.name);
  appInstance.bus = createBus({
    showEvents: true,
    reportAllErrors: true,
    log: appLog,
  });

  appInstance.info = { wePkg: { version: weVersion } }; // web-engine package

  if (typeof window !== 'undefined') {
    // Prepare for non-browser environments
    if (config.debug) {
      appInstance.debug = map(fn => fn.bind(appInstance), debugTools);
      debugTools.dndGo.call(appInstance); // setup DnD by default...
    } else {
      appInstance.debug = {};
    } // no tools unless in debug mode.. (good idea?)

    window._app = appInstance;
    if (window.document && window.document.title && config.setWindowTitle) {
      document.title = config.name;
    }
  }

  appInstance.env = buildEnv(appInstance);

  // Capture the narrowed theme in a const: `config` is a reassigned `let`, so TS
  // discards the `if (config.theme)` narrowing inside the async callback below.
  const { theme } = config;

  if (theme) {
    // the following is only needed when UI is active - which requires a theme
    await evaluateResults(
      { name: 'ThemeManagerModule', value: import('./themeManager.ts') },
      { name: 'HistoryManager', value: import('./historyManager.ts') },
      { name: 'LayerManager', value: import('./layerManager.tsx') },
    ).then(async ({ LayerManager, HistoryManager, ThemeManagerModule }) => {
      const ThemeManager = ThemeManagerModule.initThemeManager(appInstance);
      appInstance.themePack = await ThemeManager.buildTheme(theme, config.defaultTheme);

      startInitialStateListener();
      LayerManager.initLayerManager(appInstance);
      HistoryManager.initHistoryManager(appInstance);

      appInstance.destroy = () => LayerManager.destroy();
    });
  } else {
    appInstance.destroy = () => {};
  } // noop

  if (config.plugins) {
    for (const id in config.plugins) {
      try {
        const pluginConfig = config.plugins[id];
        if (appInstance.plugins[id]) {
          throw Error(`Duplicate plugin name "${id}"`);
        }
        const plugin = await setupPlugin(appInstance, id, pluginConfig);
        if (plugin) {
          appInstance.plugins[id] = plugin;
        }
      } catch (e) {
        appLog.error('Error instantiating plugin ' + id);
        appLog.error(e);
      }
    }

    for (const id in appInstance.plugins) {
      appInstance.plugins[id].init();
    }
  }

  // Fire a single, universal "app loaded" event for every target (web, visual SDK, headless SDK).
  // Deliberately sent directly here rather than through analytics2 or sdkServer, since neither
  // of those is guaranteed to be active for every deployment (e.g. analytics2 is off by default
  // for headless). This is the one signal that should always fire regardless of config.
  if (typeof window !== 'undefined') {
    appInstance.bus.send('appInsights/log', {
      name: 'app/loaded',
      properties: { deploymentHost: window.location.host },
    });
  }

  return appInstance;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export { create };
